import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { openDB } from "idb";
import { FaSync } from "react-icons/fa";
import Sidebar from "../../components/Dashboard/Sidebar";
import Header from "../../components/Dashboard/Header";
import Footer from "../../components/Dashboard/Footer";
import TrendChart from "../../components/Dashboard/Charts/TrendChart";
import LoadingSpinner from "./LoadingSpinner";
import axiosInstance from "../../utils/axiosConfig";
import QualityGateResultChart from "../../components/Dashboard/Charts/QualityGateResultChart";

function TrendAnalysis() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(1);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // State for trend analysis
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectId, setProjectId] = useState(null);

  // Loading states for different sections
  const [projectSizeLoading, setProjectSizeLoading] = useState(true);
  const [codeSmellsLoading, setCodeSmellsLoading] = useState(true);
  const [locLoading, setLocLoading] = useState(true);
  const [totalSmellsLoading, setTotalSmellsLoading] = useState(true);
  const [fitnessLoading, setFitnessLoading] = useState(true);

  // State for date click handling
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [analysisModal, setAnalysisModal] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  // Global filter states
  const [timeRange, setTimeRange] = useState('all');
  const [maxDataPoints, setMaxDataPoints] = useState(100);

  const navigate = useNavigate();

  // Initialize IndexedDB
  const dbPromise = openDB("AnalysisDB", 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("analysis")) {
        db.createObjectStore("analysis", { keyPath: "id" });
      }
    },
  });

  // Transform API data for chart consumption
  const transformDataForChart = (summaries) => {
    return summaries
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map(summary => ({
        created_at: summary.created_at,
        total_modules: summary.total_modules,
        total_classes: summary.total_classes,
        total_packages: summary.total_packages,
        total_loc: summary.total_loc,
        num_implementation_smells: summary.num_implementation_smells,
        num_design_smells: summary.num_design_smells,
        num_architecture_smells: summary.num_architecture_smells,
        num_testability_smells: summary.num_testability_smells,
        num_test_smells: summary.num_test_smells,
        total_smells: summary.total_smells,
        fitness: summary.fitness || 0.0 // Add fitness data
      }));
  };

  // Handle date click - fetch detailed analysis data and store in IndexedDB
  const handleDateClick = async (clickedDate, projectId) => {
    try {
      setAnalysisLoading(true);

      // Ensure we have a proper date format
      let formattedDate;
      if (typeof clickedDate === 'string') {
        formattedDate = clickedDate;
      } else if (clickedDate instanceof Date) {
        formattedDate = clickedDate.toISOString();
      } else {
        // If it's a timestamp or other format
        formattedDate = new Date(clickedDate).toISOString();
      }

      // Step 1: Get commit ID for the date
      const commitResponse = await axios.get(
        `${BACKEND_URL}/projects/${projectId}/get-commit-id/`,
        {
          params: {
            date: formattedDate,
          }
        }
      );

      // Rest of your existing code...
      const commitList = await axios.get(
        `${BACKEND_URL}/projects/${projectId}/commits/`
      );

      if (!commitResponse.data.commit_id) {
        throw new Error("No commit ID found for the selected date");
      }

      const commitId = commitResponse.data.commit_id;
      localStorage.setItem("commit_id", commitId);

      // Continue with visualization fetch...
      const visualizationResponse = await axios.get(
        `${BACKEND_URL}/projects/${projectId}/${commitId}/visualize/`
      );

      if (visualizationResponse.data) {
        // Store data in IndexedDB and navigate
        const { openDB } = await import('idb');
        const dbPromise = openDB('AnalysisDB', 1, {
          upgrade(db) {
            if (!db.objectStoreNames.contains('analysis')) {
              db.createObjectStore('analysis', { keyPath: 'id' });
            }
          },
        });

        const db = await dbPromise;

        await db.put('analysis', {
          id: 'analyzedData',
          data: visualizationResponse.data.data
        });

        await db.put('analysis', {
          id: 'projectId',
          data: projectId
        });

        await db.put('analysis', {
          id: 'commitList',
          data: commitList.data.commit_ids || []
        });

        await db.put('analysis', {
          id: 'visualizationMeta',
          data: {
            message: visualizationResponse.data.message,
            commit_id: visualizationResponse.data.commit_id,
            project_id: projectId,
            selected_date: clickedDate,
            fetched_at: new Date().toISOString()
          }
        });

        navigate(`/visualize/all`);
      } else {
        throw new Error("No visualization data returned from server");
      }

    } catch (error) {
      console.error("Error fetching analysis details:", error);
      console.error("Error response:", error.response?.data);

      alert(`Failed to fetch analysis details for ${clickedDate}. Error: ${error.response?.data?.message || error.message}`);
      setAnalysisModal(false);
    } finally {
      setAnalysisLoading(false);
    }
  };

  useEffect(() => {
    document.title = "DCode | Code quality trends";
  }, []);

  const projectName = localStorage.getItem("selectedProjectName");

  // Fetch trend data from API
  const fetchTrendData = async (projectId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(`/projects/${projectId}/analysis-summaries/`);

      if (response.data && response.data.summaries && response.data.summaries.length > 0) {
        const chartData = transformDataForChart(response.data.summaries);

        setTrendData(chartData);
        setProjectId(response.data.project_id);
        localStorage.setItem('selectedProjectId', String(response.data.project_id));

        // Set loading states for individual sections
        setTimeout(() => setProjectSizeLoading(false), 500);
        setTimeout(() => setLocLoading(false), 750);
        setTimeout(() => setCodeSmellsLoading(false), 1000);
        setTimeout(() => setTotalSmellsLoading(false), 1250);
        setTimeout(() => setFitnessLoading(false), 1500);
      } else {
        setError("No trend data available for this project");
      }
    } catch (err) {
      console.error("Failed to fetch trend data:", err);
      if (err.response?.status === 404) {
        setError("Project not found or no analysis summaries available");
      } else if (err.response?.status === 500) {
        setError("Server error. Please try again later.");
      } else {
        setError("Failed to load trend analysis data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Get project ID and fetch trend data
  useEffect(() => {
    async function initializeTrendAnalysis() {
      try {
        const db = await dbPromise;

        // Try to get project ID from stored analysis data
        const storedData = await db.get("analysis", "projectInfo");

        if (storedData?.data?.id) {
          localStorage.setItem('selectedProjectId', String(storedData.data.id));
          await fetchTrendData(storedData.data.id);
        }
      } catch (err) {
        console.error("Failed to initialize trend analysis:", err);
        setError("Failed to initialize trend analysis");
        setLoading(false);
      }
    }

    initializeTrendAnalysis();
  }, []);

  // Get sidebar width based on its state
  const getSidebarWidth = () => {
    if (!sidebarOpen) return "0px";
    return sidebarCollapsed ? "80px" : "280px";
  };

  const handleSidebarItemClick = (index) => {
    setActiveSidebarItem(index);
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);

  const handleSidebarCollapseChange = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  const isLargeScreen = windowWidth >= 992;

  // Refresh data function
  const refreshData = () => {
    if (projectId) {
      setProjectSizeLoading(true);
      setCodeSmellsLoading(true);
      setLocLoading(true);
      setTotalSmellsLoading(true);
      setFitnessLoading(true);
      fetchTrendData(projectId);
    }
  };

  // Close analysis modal
  const closeAnalysisModal = () => {
    setAnalysisModal(false);
    setSelectedAnalysis(null);
  };

  // Time range options
  const timeRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: '2d', label: 'Last 2 Days' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' }
  ];

  // Max points options
  const maxPointsOptions = [
    { value: 50, label: '50 points' },
    { value: 100, label: '100 points' },
    { value: 200, label: '200 points' },
    { value: 500, label: '500 points' }
  ];

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header with dynamic left margin */}
      <div
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          zIndex: 1030,
        }}
      >
        <Header
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen}
          toggleUserDropdown={toggleUserDropdown}
          pageTitle="Code quality trends"
        />
      </div>

      {/* Sidebar with collapse callback */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem}
        onItemClick={handleSidebarItemClick}
        onCollapseChange={handleSidebarCollapseChange}
      />

      {/* Mobile overlay */}
      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1040 }}
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Analysis Detail Modal */}
      {analysisModal && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Analysis Details - {selectedAnalysis?.date ? new Date(selectedAnalysis.date).toLocaleDateString() : 'Loading...'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeAnalysisModal}
                ></button>
              </div>
              <div className="modal-body">
                {analysisLoading ? (
                  <LoadingSpinner title="Loading analysis details..." />
                ) : selectedAnalysis ? (
                  <div>
                    <p><strong>Project ID:</strong> {selectedAnalysis.projectId}</p>
                    <p><strong>Analysis Date:</strong> {new Date(selectedAnalysis.date).toLocaleString()}</p>

                    {/* Display analysis data - customize based on your API response */}
                    <div className="mt-3">
                      <h6>Analysis Data:</h6>
                      <pre className="bg-light p-3 rounded">
                        {JSON.stringify(selectedAnalysis.data, null, 2)}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <p>No analysis data available.</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAnalysisModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content with dynamic margin */}
      <main
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          paddingTop: "70px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "calc(100vh - 140px)",
        }}
      >
        <div className="container-fluid px-4">
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex flex-column">
                {/* Header row with title, filters, and refresh button */}
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 mt-3">
                  <h1 className="display-9 fw-bold text-gradient mb-0">
                    Code quality trends
                  </h1>

                  {/* Filters and Controls */}
                  {!loading && !error && trendData && (
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      {/* Time Range Filter */}
                      <div className="d-flex align-items-center">
                        <label className="me-2 small text-muted" style={{ minWidth: '80px' }}>Time range:</label>
                        <select
                          value={timeRange}
                          onChange={(e) => setTimeRange(e.target.value)}
                          className="form-select form-select-sm"
                          style={{ minWidth: '140px' }}
                        >
                          {timeRangeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Max Points Filter */}
                      <div className="d-flex align-items-center">
                        <label className="me-2 small text-muted">Max points:</label>
                        <select
                          value={maxDataPoints}
                          onChange={(e) => setMaxDataPoints(Number(e.target.value))}
                          className="form-select form-select-sm"
                          style={{ width: 'auto' }}
                        >
                          {maxPointsOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Refresh Button */}
                      {projectId && (
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={refreshData}
                          disabled={loading || projectSizeLoading || codeSmellsLoading || locLoading || totalSmellsLoading || fitnessLoading}
                          title="Refresh all data"
                        >
                          <FaSync />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Project ID and Tip */}
                {projectId && (
                  <>
                    <p className="mb-2">
                     <strong>Project name: </strong><span>{projectName}</span> <strong className="header-small">Project ID: </strong> <span>{projectId}</span>
                    </p>
                    <div className="alert alert-info" role="alert">
                      <small>
                        <strong>Tip:</strong> Click on any data point in the charts below to view detailed analysis for that specific date.
                      </small>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
              <LoadingSpinner title="Loading trend analysis data..." />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="alert alert-warning" role="alert">
              <h4 className="alert-heading">Unable to Load Data</h4>
              <p>{error}</p>
              <hr />
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate("/upload")}
                >
                  Go to Upload
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={refreshData}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && trendData && (
            <div className="row g-4">
              {/* Fitness Score Chart */}
              <div className="col-md-12">
                <div className="card p-3 shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Code fitness score over time</h5>

                  </div>

                  {fitnessLoading ? (
                    <LoadingSpinner title="Loading fitness data..." />
                  ) : (
                    <TrendChart
                      data={trendData}
                      dataKeys={["fitness"]}
                      title=""
                      colors={["#8b5cf6"]}
                      projectId={projectId}
                      onDateClick={handleDateClick}
                      timeRange={timeRange}
                      maxDataPoints={maxDataPoints}
                      yAxisDomain={[0, 1]}
                      formatYAxis={(value) => `${(value * 100).toFixed(0)}%`}
                    />
                  )}
                </div>
              </div>

              {/* Project Size Over Time */}
              <div className="col-md-6 col-12 col-sm-12 col-lg-6">
                <div className="card p-3 shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Project size over time</h5>
                  </div>

                  {projectSizeLoading ? (
                    <LoadingSpinner title="Loading project size metrics..." />
                  ) : (
                    <TrendChart
                      data={trendData}
                      dataKeys={["total_modules", "total_classes", "total_packages"]}
                      title=""
                      colors={["#2563eb", "#059669", "#dc2626"]}
                      projectId={projectId}
                      onDateClick={handleDateClick}
                      timeRange={timeRange}
                      maxDataPoints={maxDataPoints}
                    />
                  )}
                </div>
              </div>

              {/* Lines of Code Over Time */}
              <div className="col-md-6 col-12 col-lg-6 col-sm-12">
                <div className="card p-3 shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Lines of code over time</h5>
                  </div>

                  {locLoading ? (
                    <LoadingSpinner title="Loading lines of code data..." />
                  ) : (
                    <TrendChart
                      data={trendData}
                      dataKeys={["total_loc"]}
                      title=""
                      colors={["#7c3aed"]}
                      projectId={projectId}
                      onDateClick={handleDateClick}
                      timeRange={timeRange}
                      maxDataPoints={maxDataPoints}
                    />
                  )}
                </div>
              </div>

              {/* Code Smells Distribution Over Time */}
              <div className="col-md-12">
                <div className="card p-3 shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Code smells distribution over time</h5>
                  </div>

                  {codeSmellsLoading ? (
                    <LoadingSpinner title="Loading code smells distribution..." />
                  ) : (
                    <TrendChart
                      data={trendData}
                      dataKeys={[
                        "num_implementation_smells",
                        "num_design_smells",
                        "num_architecture_smells",
                        "num_testability_smells",
                        "num_test_smells"
                      ]}
                      title=""
                      colors={["#ef4444", "#f59e0b", "#8b5cf6", "#06b6d4", "#10b981"]}
                      projectId={projectId}
                      onDateClick={handleDateClick}
                      timeRange={timeRange}
                      maxDataPoints={maxDataPoints}
                    />
                  )}
                </div>
              </div>

              <div className="col-md-12">
                <div className="card p-3 shadow-sm">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Quality gate status</h5>
                  </div>

                  {codeSmellsLoading ? (
                    <LoadingSpinner title="Loading code smells distribution..." />
                  ) : (
                    <QualityGateResultChart
  projectId={projectId}
  timeRange={timeRange}
  onDateClick={(entry) => {
    if (!entry?.commit_id) return;

    navigate(`/project/quality-delta-explorer/`, {
      state: {
        projectId,
        selectedCommitId: entry.commit_id,
        fromQualityGate: true,
      }
    });
  }}
/>
                  )}
                </div>
              </div>

              
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
}

export default TrendAnalysis;